import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, Copy, Eye, EyeOff, Move,
  Monitor, Tablet, Smartphone, X, ChevronUp, ChevronDown,
  Layout, Image as ImageIcon, Type, Square, Grid3x3, BarChart3,
  Minus, Code, HelpCircle, Quote, Video, MapPin, Mail, Users, Image,
  AlertCircle, CheckCircle2, Search, GripVertical, ExternalLink,
  Undo2, Redo2, Zap, History, Crown, FolderOpen, Award,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, formatApiError } from '../../lib/api';
import BlockRenderer, { BlocksRenderer } from '../../components/BlockRenderer';
import RichTextEditor from '../../components/RichTextEditor';
import ImageUploader from '../../components/ImageUploader';
import DocumentUploader from '../../components/DocumentUploader';
import MediaLibraryPicker from '../../components/MediaLibraryPicker';
import VersionHistoryModal from '../../components/VersionHistoryModal';

/* ============================================================
   BLOCK TYPE METADATA — must mirror AdminPageEditorPage.js
   ============================================================ */
const BLOCK_TYPES = [
  { kind: 'hero', label: 'Hero', desc: 'Encabezado grande con título + subtítulo + botón', icon: Layout, group: 'Encabezados' },
  { kind: 'president_message', label: 'Mensaje de la Presidencia', desc: 'Retrato + mensaje + firma', icon: Quote, group: 'Encabezados' },
  { kind: 'text', label: 'Texto rico', desc: 'Párrafos con formato', icon: Type, group: 'Contenido' },
  { kind: 'image', label: 'Imagen', desc: 'Foto con caption opcional', icon: ImageIcon, group: 'Contenido' },
  { kind: 'cta', label: 'Llamada a la acción', desc: 'Botón destacado', icon: Square, group: 'Conversión' },
  { kind: 'cards', label: 'Tarjetas', desc: 'Hasta 3 columnas', icon: Grid3x3, group: 'Contenido' },
  { kind: 'stats', label: 'Estadísticas', desc: 'Números destacados', icon: BarChart3, group: 'Contenido' },
  { kind: 'gallery', label: 'Galería', desc: 'Cuadrícula de imágenes', icon: Image, group: 'Contenido' },
  { kind: 'video', label: 'Video', desc: 'Embed YouTube/Vimeo', icon: Video, group: 'Contenido' },
  { kind: 'accordion', label: 'FAQ / Acordeón', desc: 'Preguntas y respuestas', icon: HelpCircle, group: 'Contenido' },
  { kind: 'testimonials', label: 'Testimonios', desc: 'Citas de clientes', icon: Quote, group: 'Contenido' },
  { kind: 'map', label: 'Mapa', desc: 'Mapa embebido', icon: MapPin, group: 'Conversión' },
  { kind: 'contact_form', label: 'Formulario de contacto', desc: 'Form integrado', icon: Mail, group: 'Conversión' },
  { kind: 'embedded_member_list', label: 'Lista de miembros', desc: 'Cards del directorio', icon: Users, group: 'Datos' },
  { kind: 'board_members', label: 'Consejo Directivo', desc: 'Sección con la rejilla del consejo (dinámica)', icon: Crown, group: 'Datos' },
  { kind: 'past_presidents', label: 'Expresidentes', desc: 'Premio Malintzin + línea de presidentes (dinámica)', icon: Award, group: 'Datos' },
  { kind: 'spacer', label: 'Espaciador', desc: 'Espacio vertical', icon: Minus, group: 'Diseño' },
  { kind: 'html', label: 'HTML libre', desc: 'Bloque crudo (avanzado)', icon: Code, group: 'Diseño' },
];

function defaultDataFor(kind) {
  switch (kind) {
    case 'hero':
      return { label_es: '', label_en: '', title_es: 'Nuevo título', title_en: 'New title', subtitle_es: '', subtitle_en: '', image_url: '', cta_text_es: '', cta_text_en: '', cta_url: '' };
    case 'president_message':
      return {
        role_es: 'Mensaje de la Presidencia',
        role_en: 'Message from the Presidency',
        body_es: '<p>Escribe aquí el mensaje de la presidenta o presidente.</p>',
        body_en: '<p>Write the president\'s message here.</p>',
        name: '',
        title_es: 'Presidenta del CMIC',
        title_en: 'President of CMIC',
        period: 'Periodo 2026',
        photo_url: '',
      };
    case 'text':
      return { html_es: '<p>Escribe aquí tu contenido en español.</p>', html_en: '<p>Write your content here in English.</p>' };
    case 'image':
      return { url: '', alt_es: '', alt_en: '', caption_es: '', caption_en: '', full_width: false };
    case 'cta':
      return { title_es: 'Únete a nosotros', title_en: 'Join us', body_es: '', body_en: '', button_text_es: 'Conocer más', button_text_en: 'Learn more', button_url: '/es', variant: 'light' };
    case 'cards':
      return { title_es: '', title_en: '', items: [{ title_es: 'Tarjeta 1', title_en: 'Card 1', body_es: 'Descripción', body_en: 'Description' }, { title_es: 'Tarjeta 2', title_en: 'Card 2', body_es: 'Descripción', body_en: 'Description' }, { title_es: 'Tarjeta 3', title_en: 'Card 3', body_es: 'Descripción', body_en: 'Description' }] };
    case 'stats':
      return { items: [{ value: '97', label_es: 'Miembros', label_en: 'Members' }, { value: '42', label_es: 'Años', label_en: 'Years' }] };
    case 'gallery':
      return { title_es: '', title_en: '', items: [] };
    case 'video':
      return { title_es: '', title_en: '', url: '', caption_es: '', caption_en: '' };
    case 'accordion':
      return { title_es: 'Preguntas frecuentes', title_en: 'FAQ', items: [{ q_es: '¿Pregunta?', q_en: 'Question?', a_es: 'Respuesta.', a_en: 'Answer.' }] };
    case 'testimonials':
      return { title_es: '', title_en: '', items: [{ quote: 'Testimonio aquí.', author: 'Nombre, Cargo' }] };
    case 'map':
      return { title_es: 'Encuéntranos', title_en: 'Find us', embed_url: '', latitude: '', longitude: '', zoom: 14, caption_es: '', caption_en: '' };
    case 'contact_form':
      return { title_es: 'Contáctanos', title_en: 'Contact us', subtitle_es: '', subtitle_en: '' };
    case 'embedded_member_list':
      return { title_es: 'Nuestros intérpretes', title_en: 'Our interpreters', limit: 9, layout: 'grid', filter_language: '', filter_city: '', show_view_all: true };
    case 'board_members':
      return {};
    case 'past_presidents':
      return {};
    case 'periodicals_list':
      return {};
    case 'spacer':
      return { height: 60 };
    case 'html':
      return { html_es: '', html_en: '' };
    default:
      return {};
  }
}

const VIEWPORTS = {
  desktop: { label: 'Escritorio', icon: Monitor, width: '100%', maxWidth: '1280px' },
  tablet: { label: 'Tablet', icon: Tablet, width: '768px', maxWidth: '768px' },
  mobile: { label: 'Móvil', icon: Smartphone, width: '375px', maxWidth: '375px' },
};

const PAGE_LABELS = {
  home: 'Inicio', about: 'Quiénes somos', membership: 'Membresía',
  resources: 'Recursos', contact: 'Contacto', footer: 'Pie de página',
};

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function AdminVisualEditorPage() {
  const { page_key } = useParams();
  const navigate = useNavigate();
  const [blocks, _setBlocks] = useState([]);          // current state (mutable in editor)
  const [savedBlocks, setSavedBlocks] = useState([]); // last server-confirmed state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerInsertIndex, setPickerInsertIndex] = useState(null); // null = append
  const [viewport, setViewport] = useState('desktop');
  const [feedback, setFeedback] = useState(null);
  const [pageMeta, setPageMeta] = useState(null); // for custom pages
  const [showHistory, setShowHistory] = useState(false);

  // ---- History (undo / redo) ----
  const historyPast = useRef([]);          // snapshots BEFORE current
  const historyFuture = useRef([]);        // snapshots AFTER (when user did undo)
  const HISTORY_MAX = 40;
  const [historyTick, setHistoryTick] = useState(0); // bump to re-render Undo/Redo buttons
  const skipHistoryRef = useRef(false);    // when true, next setBlocks does NOT push history

  // Wrapper that pushes to history before mutating
  const setBlocks = (updater) => {
    let pushed = false;
    _setBlocks((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Don't record no-ops (same JSON)
      if (!skipHistoryRef.current) {
        const a = JSON.stringify(prev);
        const b = JSON.stringify(next);
        if (a !== b) {
          historyPast.current.push(prev);
          if (historyPast.current.length > HISTORY_MAX) historyPast.current.shift();
          historyFuture.current = []; // any new edit clears redo stack
          pushed = true;
        }
      }
      skipHistoryRef.current = false;
      return next;
    });
    // Bump tick OUTSIDE the updater so React doesn't warn about nested setState.
    if (pushed) setHistoryTick((t) => t + 1);
  };
  const canUndo = historyPast.current.length > 0;
  const canRedo = historyFuture.current.length > 0;
  const undo = () => {
    if (!canUndo) return;
    _setBlocks((cur) => {
      const prev = historyPast.current.pop();
      historyFuture.current.push(cur);
      if (historyFuture.current.length > HISTORY_MAX) historyFuture.current.shift();
      return prev;
    });
    setHistoryTick((t) => t + 1);
  };
  const redo = () => {
    if (!canRedo) return;
    _setBlocks((cur) => {
      const next = historyFuture.current.pop();
      historyPast.current.push(cur);
      if (historyPast.current.length > HISTORY_MAX) historyPast.current.shift();
      return next;
    });
    setHistoryTick((t) => t + 1);
  };

  // ---- Auto-save (optional toggle, persisted in localStorage) ----
  const [autoSave, setAutoSave] = useState(() => {
    try { return localStorage.getItem('cmic_visual_autosave') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cmic_visual_autosave', autoSave ? '1' : '0'); } catch {}
  }, [autoSave]);

  const dirty = useMemo(() => JSON.stringify(blocks) !== JSON.stringify(savedBlocks), [blocks, savedBlocks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function refresh() {
    setLoading(true);
    try {
      const [{ data: bd }, pagesRes] = await Promise.all([
        api.get(`/admin/pages/${page_key}/blocks`),
        api.get('/admin/pages').catch(() => ({ data: [] })),
      ]);
      const arr = Array.isArray(bd) ? bd : [];
      // Use the raw setter directly to bypass the history wrapper — we don't want
      // the server-confirmed snapshot to be recorded as an "undoable" user action.
      _setBlocks(arr);
      setSavedBlocks(arr);
      // Clear history on (re)load
      historyPast.current = [];
      historyFuture.current = [];
      setHistoryTick((t) => t + 1);
      const meta = (pagesRes.data || []).find((p) => p.page_key === page_key);
      setPageMeta(meta || null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [page_key]);

  async function importExisting() {
    try {
      const { data } = await api.post(`/admin/pages/${page_key}/import-existing`);
      await refresh();
      setFeedback({ type: 'success', message: `Contenido actual importado (${data.imported || 0} bloque${(data.imported || 0) === 1 ? '' : 's'}).` });
    } catch (e) {
      setFeedback({ type: 'error', message: formatApiError(e.response?.data?.detail) || 'No hay contenido predefinido para esta página.' });
    }
  }

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Keyboard shortcuts: Cmd/Ctrl + Z = undo, +Shift+Z or +Y = redo, +S = save
  useEffect(() => {
    const onKey = (e) => {
      // Don't capture while user is typing inside an input/textarea/contentEditable
      const target = e.target;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      const isMac = (typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC'));
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) {
        if (isTyping) return; // let browser handle text undo
        e.preventDefault(); undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        if (isTyping) return;
        e.preventDefault(); redo();
      } else if (e.key === 's') {
        e.preventDefault();
        if (dirty && !saving) save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, saving]);

  // Auto-save: when enabled, debounce save 2s after last change.
  useEffect(() => {
    if (!autoSave || !dirty || saving || loading) return;
    const t = setTimeout(() => { save(); }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSave, dirty, blocks, saving, loading]);

  const selected = blocks.find((b) => b.id === selectedId);

  function updateBlockLocal(id, patch) {
    setBlocks((bs) => bs.map((b) => b.id === id ? { ...b, ...patch } : b));
  }
  function updateBlockData(id, dataPatch) {
    setBlocks((bs) => bs.map((b) => b.id === id ? { ...b, data: { ...b.data, ...dataPatch } } : b));
  }

  function moveBlock(id, dir) {
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === id);
      if (idx < 0) return bs;
      const target = dir === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= bs.length) return bs;
      return arrayMove(bs, idx, target);
    });
  }
  function duplicateBlock(id) {
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === id);
      if (idx < 0) return bs;
      const src = bs[idx];
      const copy = { ...src, id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, data: JSON.parse(JSON.stringify(src.data || {})) };
      return [...bs.slice(0, idx + 1), copy, ...bs.slice(idx + 1)];
    });
  }
  function deleteBlock(id) {
    if (!window.confirm('¿Eliminar este bloque?')) return;
    setBlocks((bs) => bs.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  }
  function toggleVisible(id) {
    setBlocks((bs) => bs.map((b) => b.id === id ? { ...b, visible: !b.visible } : b));
  }

  function addBlock(kind, insertAt = null) {
    const newBlock = {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      page_key,
      kind,
      data: defaultDataFor(kind),
      visible: true,
      sort_order: 0,
    };
    setBlocks((bs) => {
      if (insertAt === null || insertAt >= bs.length) return [...bs, newBlock];
      return [...bs.slice(0, insertAt), newBlock, ...bs.slice(insertAt)];
    });
    setShowPicker(false);
    setPickerInsertIndex(null);
    setSelectedId(newBlock.id);
  }

  async function save() {
    setSaving(true);
    try {
      // 1. Identify deletions (in saved but not in blocks)
      const currentIds = new Set(blocks.map((b) => b.id));
      const toDelete = savedBlocks.filter((b) => !currentIds.has(b.id));
      // 2. Identify creations (tmp- prefix or not in saved)
      const savedIds = new Set(savedBlocks.map((b) => b.id));
      const toCreate = blocks.filter((b) => b.id.startsWith('tmp-') || !savedIds.has(b.id));
      // 3. Identify updates (existing id, data/visible/kind changed)
      const toUpdate = blocks.filter((b) => {
        if (b.id.startsWith('tmp-')) return false;
        const old = savedBlocks.find((s) => s.id === b.id);
        if (!old) return false;
        return JSON.stringify({ d: b.data, v: b.visible, k: b.kind }) !== JSON.stringify({ d: old.data, v: old.visible, k: old.kind });
      });

      // Execute deletes
      await Promise.all(toDelete.map((b) => api.delete(`/admin/pages/blocks/${b.id}`)));

      // Execute creates (sequentially to preserve sort order on backend)
      const tmpToReal = new Map();
      for (const b of toCreate) {
        const { data } = await api.post(`/admin/pages/${page_key}/blocks`, {
          page_key, kind: b.kind, data: b.data, visible: b.visible,
        });
        tmpToReal.set(b.id, data.id);
      }

      // Execute updates
      await Promise.all(toUpdate.map((b) => api.put(`/admin/pages/blocks/${b.id}`, {
        data: b.data, visible: b.visible, kind: b.kind,
      })));

      // Reorder full list (using real ids)
      const orderedIds = blocks.map((b) => tmpToReal.get(b.id) || b.id);
      if (orderedIds.length > 0) {
        await api.put(`/admin/pages/${page_key}/blocks/reorder`, { ids: orderedIds });
      }

      // Refresh from server
      await refresh();
      // Snapshot the new state into version history (fire-and-forget — failure
      // doesn't break the save flow; backend dedupes identical snapshots).
      try {
        await api.post(`/admin/pages/${page_key}/versions`, { change_summary: '' });
      } catch (_) { /* non-critical */ }
      setFeedback({ type: 'success', message: 'Página guardada exitosamente.' });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error al guardar: ' + formatApiError(err) });
    } finally {
      setSaving(false);
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks((bs) => {
      const oldIdx = bs.findIndex((b) => b.id === active.id);
      const newIdx = bs.findIndex((b) => b.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return bs;
      return arrayMove(bs, oldIdx, newIdx);
    });
  }

  if (loading) {
    return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>;
  }

  const pageLabel = pageMeta?.label_es || PAGE_LABELS[page_key] || page_key;
  const isCustom = pageMeta?.is_custom;
  const previewUrl = isCustom && pageMeta?.route_slug
    ? `/es/p/${pageMeta.route_slug}`
    : (pageMeta?.public_url || (page_key === 'footer' ? '/es' : '/es'));

  return (
    <div className="h-screen w-full flex flex-col bg-zinc-100 overflow-hidden text-zinc-950 font-sans -m-4 sm:-m-6 lg:-m-8" data-testid="visual-editor">
      {/* ============ TOP TOOLBAR ============ */}
      <div className="h-14 w-full bg-white border-b border-zinc-200 flex items-center justify-between px-4 shrink-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/pages')} data-testid="toolbar-back-btn" className="p-2 rounded-md hover:bg-zinc-100 text-zinc-600">
            <ArrowLeft size={18} />
          </button>
          <div className="flex flex-col">
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">Editando</span>
            <span className="text-sm font-semibold text-zinc-900" data-testid="toolbar-page-title">{pageLabel}</span>
          </div>
        </div>

        {/* Viewport switcher + Undo/Redo group */}
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-zinc-100 rounded-lg p-0.5" data-testid="viewport-switcher">
            {Object.entries(VIEWPORTS).map(([key, vp]) => {
              const Icon = vp.icon;
              const active = viewport === key;
              return (
                <button
                  key={key}
                  onClick={() => setViewport(key)}
                  data-testid={`viewport-${key}`}
                  className={`p-1.5 rounded-md transition-colors ${active ? 'bg-white text-cmic-600 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                  title={vp.label}
                >
                  <Icon size={16} />
                </button>
              );
            })}
          </div>
          <div className="flex items-center bg-zinc-100 rounded-lg p-0.5" data-testid="history-controls">
            <button
              onClick={undo}
              disabled={!canUndo}
              data-testid="toolbar-undo-btn"
              className="p-1.5 rounded-md text-zinc-600 hover:bg-white hover:text-cmic-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Deshacer (⌘Z)"
              aria-label="Deshacer"
            >
              <Undo2 size={16} />
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              data-testid="toolbar-redo-btn"
              className="p-1.5 rounded-md text-zinc-600 hover:bg-white hover:text-cmic-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Rehacer (⌘⇧Z)"
              aria-label="Rehacer"
            >
              <Redo2 size={16} />
            </button>
          </div>
          <button
            onClick={() => setShowHistory(true)}
            data-testid="toolbar-history-btn"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-cmic-600 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors"
            title="Historial de versiones"
          >
            <History size={14} /> Historial
          </button>
        </div>

        <div className="flex items-center gap-3">
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-cmic-600" data-testid="toolbar-preview-link">
            <ExternalLink size={12} /> Vista pública
          </a>
          <button
            onClick={() => setAutoSave((v) => !v)}
            data-testid="toolbar-autosave-toggle"
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${autoSave ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'text-zinc-500 hover:bg-zinc-100 border border-transparent'}`}
            title={autoSave ? 'Autoguardado activado — desactivar' : 'Activar autoguardado (guarda 2s después del último cambio)'}
            aria-pressed={autoSave}
          >
            <Zap size={11} className={autoSave ? 'fill-emerald-500' : ''} /> Auto
          </button>
          {saving ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-cmic-700" data-testid="toolbar-status-saving">
              <Loader2 size={12} className="animate-spin" /> Guardando…
            </span>
          ) : dirty ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-700" data-testid="toolbar-status-text">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              {autoSave ? 'Guardando pronto…' : 'Cambios sin guardar'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="toolbar-status-clean">
              <CheckCircle2 size={12} /> Guardado
            </span>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="toolbar-save-btn"
            className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-1.5 rounded-md text-sm font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </button>
        </div>
      </div>

      {/* ============ WORKSPACE ============ */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT OUTLINE PANEL */}
        <div className="w-64 bg-white border-r border-zinc-200 flex flex-col shrink-0 overflow-hidden" data-testid="outline-panel">
          <div className="h-12 border-b border-zinc-200 flex items-center justify-between px-4 shrink-0">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Estructura</span>
            <span className="text-xs text-zinc-400">{blocks.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {blocks.map((b) => (
                  <OutlineItem
                    key={b.id}
                    block={b}
                    selected={b.id === selectedId}
                    onSelect={() => setSelectedId(b.id)}
                    onToggleVisible={() => toggleVisible(b.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {blocks.length === 0 && (
              <p className="text-xs text-zinc-400 px-2 py-4 text-center">Sin bloques aún.</p>
            )}
          </div>
          <button
            onClick={() => { setPickerInsertIndex(null); setShowPicker(true); }}
            data-testid="outline-add-btn"
            className="m-2 inline-flex items-center justify-center gap-1.5 border border-dashed border-cmic-300 text-cmic-700 hover:bg-cmic-50 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
          >
            <Plus size={14} /> Agregar bloque
          </button>
        </div>

        {/* CANVAS */}
        <div className="flex-1 overflow-auto bg-zinc-100 p-6" data-testid="canvas-area">
          {feedback && (
            <div className={`max-w-3xl mx-auto mb-4 px-4 py-3 rounded-xl text-sm border flex items-center gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
              {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              {feedback.message}
            </div>
          )}
          <div className="mx-auto bg-white shadow-sm ring-1 ring-zinc-200 transition-all duration-300" style={{ width: VIEWPORTS[viewport].width, maxWidth: VIEWPORTS[viewport].maxWidth }} data-testid={`canvas-${viewport}`}>
            {blocks.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-300 rounded-xl p-12 m-8 flex flex-col items-center text-center bg-zinc-50" data-testid="canvas-empty-state">
                <Plus size={48} className="text-zinc-400 mb-4" />
                <h2 className="text-xl font-medium text-zinc-900 mb-2">Página vacía</h2>
                <p className="text-sm text-zinc-500 mb-6">Comienza agregando tu primer bloque de contenido a esta página.</p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button onClick={() => { setPickerInsertIndex(null); setShowPicker(true); }} data-testid="canvas-empty-add-btn" className="bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2 rounded-md font-medium shadow-sm inline-flex items-center gap-2">
                    <Plus size={16} /> Agregar primer bloque
                  </button>
                  <button onClick={importExisting} data-testid="canvas-import-existing-btn" className="bg-white hover:bg-zinc-50 text-cmic-700 border border-cmic-200 px-4 py-2 rounded-md font-medium inline-flex items-center gap-2">
                    <History size={16} /> Importar contenido actual
                  </button>
                </div>
              </div>
            ) : (
              <>
                <InsertionHandle onClick={() => { setPickerInsertIndex(0); setShowPicker(true); }} testid={`insertion-handle-0`} />
                {blocks.map((b, i) => (
                  <React.Fragment key={b.id}>
                    <CanvasBlock
                      block={b}
                      isSelected={b.id === selectedId}
                      onSelect={() => setSelectedId(b.id)}
                      onMoveUp={() => moveBlock(b.id, 'up')}
                      onMoveDown={() => moveBlock(b.id, 'down')}
                      onDuplicate={() => duplicateBlock(b.id)}
                      onDelete={() => deleteBlock(b.id)}
                      onToggleVisible={() => toggleVisible(b.id)}
                      canMoveUp={i > 0}
                      canMoveDown={i < blocks.length - 1}
                      onInlineEdit={(blockId, field, value) => updateBlockData(blockId, { [field]: value })}
                    />
                    <InsertionHandle onClick={() => { setPickerInsertIndex(i + 1); setShowPicker(true); }} testid={`insertion-handle-${i + 1}`} />
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        </div>

        {/* RIGHT EDIT PANEL */}
        {selected && (
          <EditPanel
            block={selected}
            onClose={() => setSelectedId(null)}
            onUpdateData={(patch) => updateBlockData(selected.id, patch)}
          />
        )}
      </div>

      {/* BLOCK PICKER MODAL */}
      {showPicker && (
        <BlockPickerModal
          onClose={() => { setShowPicker(false); setPickerInsertIndex(null); }}
          onPick={(kind) => addBlock(kind, pickerInsertIndex)}
        />
      )}

      {/* VERSION HISTORY MODAL */}
      {showHistory && (
        <VersionHistoryModal
          pageKey={page_key}
          onClose={() => setShowHistory(false)}
          onRestored={async () => {
            // Server already replaced blocks with restored snapshot.
            // Refresh editor state (which also clears local history stack).
            await refresh();
            setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}

/* ============================================================
   OUTLINE ITEM (drag-sortable list row)
   ============================================================ */
function OutlineItem({ block, selected, onSelect, onToggleVisible }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const meta = BLOCK_TYPES.find((t) => t.kind === block.kind);
  const Icon = meta?.icon || Square;
  return (
    <div ref={setNodeRef} style={style} className={`group flex items-center gap-2 p-2 rounded-md text-sm transition-colors ${selected ? 'bg-cmic-50 text-cmic-700 ring-1 ring-cmic-200' : 'hover:bg-zinc-100 text-zinc-700'}`} data-testid={`outline-item-${block.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab text-zinc-400 hover:text-zinc-600 touch-none" aria-label="Mover bloque">
        <GripVertical size={14} />
      </button>
      <button onClick={onSelect} className="flex-1 flex items-center gap-2 text-left min-w-0">
        <Icon size={14} className={selected ? 'text-cmic-600' : 'text-zinc-400'} />
        <span className={`truncate text-xs ${selected ? 'font-medium' : ''} ${!block.visible ? 'line-through opacity-50' : ''}`}>{meta?.label || block.kind}</span>
      </button>
      <button onClick={onToggleVisible} className="text-zinc-400 hover:text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity" title={block.visible ? 'Ocultar' : 'Mostrar'}>
        {block.visible ? <Eye size={12} /> : <EyeOff size={12} />}
      </button>
    </div>
  );
}

/* ============================================================
   CANVAS BLOCK (live preview + click + floating action bar)
   ============================================================ */
function CanvasBlock({ block, isSelected, onSelect, onMoveUp, onMoveDown, onDuplicate, onDelete, onToggleVisible, canMoveUp, canMoveDown, onInlineEdit }) {
  return (
    <div
      className={`relative group cursor-pointer ${!block.visible ? 'opacity-40' : ''} ${isSelected ? 'ring-2 ring-cmic-500 ring-inset' : 'hover:ring-2 hover:ring-cmic-300 hover:ring-dashed hover:ring-inset'} transition-all`}
      data-testid={`canvas-block-${block.id}`}
      onClick={(e) => {
        // Click on the wrapper selects the block — but if user clicked an editable text element,
        // let the contentEditable swallow it (we already do stopPropagation in <Editable>).
        // Also ignore clicks on the floating action bar (its buttons stop propagation).
        onSelect();
      }}
    >
      {/* Block content — inline-editable in canvas mode, navigation links neutralized */}
      <div>
        <BlocksRenderer blocks={[block]} locale="es" editMode={onInlineEdit} />
      </div>
      {/* Floating action bar (visible when selected or hover) */}
      <div
        className={`absolute -top-3 right-4 bg-cmic-600 text-white rounded-md shadow-lg flex items-center gap-0.5 p-0.5 z-20 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionButton onClick={onMoveUp} disabled={!canMoveUp} icon={ChevronUp} title="Mover arriba" testid={`block-action-up-${block.id}`} />
        <ActionButton onClick={onMoveDown} disabled={!canMoveDown} icon={ChevronDown} title="Mover abajo" testid={`block-action-down-${block.id}`} />
        <ActionButton onClick={onDuplicate} icon={Copy} title="Duplicar" testid={`block-action-duplicate-${block.id}`} />
        <ActionButton onClick={onToggleVisible} icon={block.visible ? Eye : EyeOff} title={block.visible ? 'Ocultar' : 'Mostrar'} testid={`block-action-toggle-${block.id}`} />
        <ActionButton onClick={onDelete} icon={Trash2} title="Eliminar" testid={`block-action-delete-${block.id}`} danger />
      </div>
    </div>
  );
}

function ActionButton({ onClick, icon: Icon, title, danger, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      data-testid={testid}
      className={`p-1.5 rounded transition-colors ${danger ? 'hover:bg-red-500/30' : 'hover:bg-white/20'} disabled:opacity-30 disabled:cursor-not-allowed`}
      title={title}
      aria-label={title}
    >
      <Icon size={14} />
    </button>
  );
}

/* ============================================================
   INSERTION HANDLE (the "+" between blocks)
   ============================================================ */
function InsertionHandle({ onClick, testid }) {
  return (
    <div className="relative h-6 flex items-center justify-center opacity-0 hover:opacity-100 z-20 transition-opacity">
      <div className="absolute inset-x-0 h-[2px] bg-cmic-500/40" />
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="relative bg-cmic-600 hover:bg-cmic-700 text-white rounded-full px-2.5 py-1 shadow-md inline-flex items-center gap-1 text-[11px] font-medium z-10 transition-all hover:scale-105"
      >
        <Plus size={12} /> Añadir bloque
      </button>
    </div>
  );
}

/* ============================================================
   BLOCK PICKER MODAL
   ============================================================ */
function BlockPickerModal({ onClose, onPick }) {
  const [search, setSearch] = useState('');
  const filtered = BLOCK_TYPES.filter((t) =>
    !search ||
    t.label.toLowerCase().includes(search.toLowerCase()) ||
    t.desc.toLowerCase().includes(search.toLowerCase()) ||
    t.group.toLowerCase().includes(search.toLowerCase())
  );
  // Group by category
  const grouped = filtered.reduce((acc, t) => {
    (acc[t.group] = acc[t.group] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="block-picker-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-200 flex justify-between items-center gap-4">
          <h2 className="text-lg font-semibold text-zinc-900">Seleccionar bloque</h2>
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar bloques…"
              data-testid="block-picker-search"
              className="w-full bg-zinc-100 border-none rounded-md pl-9 pr-3 py-2 text-sm focus:bg-white focus:ring-2 focus:ring-cmic-500/30 outline-none"
            />
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-6">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="mb-6 last:mb-0">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-3">{group}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {items.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      type="button"
                      key={t.kind}
                      onClick={() => onPick(t.kind)}
                      data-testid={`block-type-${t.kind}`}
                      className="text-left p-4 border border-zinc-200 rounded-lg hover:border-cmic-500 hover:bg-cmic-50/50 cursor-pointer transition-all group"
                    >
                      <Icon size={20} className="text-zinc-400 group-hover:text-cmic-600 mb-2" />
                      <div className="text-sm font-medium text-zinc-900 group-hover:text-cmic-700 mb-0.5">{t.label}</div>
                      <div className="text-[11px] text-zinc-500 leading-tight">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-zinc-400 text-sm py-8">No se encontraron bloques. Prueba con otra búsqueda.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   EDIT PANEL (right sidebar) — dynamic form based on block kind
   ============================================================ */
function EditPanel({ block, onClose, onUpdateData }) {
  const meta = BLOCK_TYPES.find((t) => t.kind === block.kind);
  return (
    <div className="w-80 bg-white border-l border-zinc-200 flex flex-col shrink-0 overflow-hidden shadow-lg" data-testid="editor-panel">
      <div className="flex items-center justify-between px-4 h-12 border-b border-zinc-200 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {meta?.icon ? <meta.icon size={14} className="text-cmic-600 flex-shrink-0" /> : null}
          <span className="text-sm font-semibold text-zinc-900 truncate">{meta?.label || block.kind}</span>
        </div>
        <button onClick={onClose} data-testid="editor-close-btn" className="text-zinc-400 hover:text-zinc-700 p-1 rounded">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <BlockFields kind={block.kind} data={block.data || {}} onUpdate={onUpdateData} />
        </div>
        {/* Vista previa en vivo — se actualiza al editar. Se renderiza con el mismo
            componente público para que coincida exactamente con el sitio. */}
        <LivePreview block={block} />
      </div>
    </div>
  );
}

function LivePreview({ block }) {
  return (
    <div className="border-t border-zinc-200 bg-zinc-50" data-testid="edit-panel-live-preview">
      <div className="px-4 py-2.5 border-b border-zinc-200 bg-white flex items-center gap-2">
        <Eye size={12} className="text-cmic-600" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Vista previa en vivo</span>
      </div>
      <div className="p-3">
        <div
          className="bg-white rounded-md border border-zinc-200 overflow-hidden"
          style={{
            // Scale the preview down so wider sections (hero, stats) fit comfortably
            // inside the 320px sidebar. The visual ratio is preserved.
            transform: 'scale(0.32)',
            transformOrigin: 'top left',
            width: '900px',
            height: '380px',
            pointerEvents: 'none',
          }}
        >
          <BlockRenderer block={block} locale="es" editMode={false} />
        </div>
        <p className="text-[10px] text-zinc-400 mt-2 italic">
          La vista previa se reduce al 32% para caber en el panel. Usa el botón "Vista pública" para verla a tamaño real.
        </p>
      </div>
    </div>
  );
}

function Bilingual({ labelEs, labelEn, valueEs, valueEn, onEs, onEn, multiline }) {
  const InputTag = multiline ? 'textarea' : 'input';
  return (
    <div className="space-y-2">
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider flex items-center gap-1">
          {labelEs} <span className="text-zinc-400 normal-case">(ES)</span>
        </label>
        <InputTag value={valueEs || ''} onChange={(e) => onEs(e.target.value)} rows={multiline ? 3 : undefined} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none" />
      </div>
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider flex items-center gap-1">
          {labelEn} <span className="text-zinc-400 normal-case">(EN)</span>
        </label>
        <InputTag value={valueEn || ''} onChange={(e) => onEn(e.target.value)} rows={multiline ? 3 : undefined} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none" />
      </div>
    </div>
  );
}

// URL input with a "pick from Archivos (media library)" button. Used for button
// links so admins can link a PDF/image without typing the path manually.
function UrlField({ value, onChange, placeholder }) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="mt-1 flex gap-2">
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid="block-url-input"
        className="flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none" />
      <button type="button" onClick={() => setPicking(true)} title="Elegir de Archivos"
        data-testid="block-url-pick-btn"
        className="px-2.5 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-50 inline-flex items-center">
        <FolderOpen size={15} />
      </button>
      {picking && (
        <MediaLibraryPicker
          title="Elegir archivo o imagen"
          onClose={() => setPicking(false)}
          onSelect={(f) => { onChange(f.url); setPicking(false); }}
        />
      )}
    </div>
  );
}

function BlockFields({ kind, data, onUpdate }) {
  const set = (k) => (v) => onUpdate({ [k]: v });

  if (kind === 'hero') {
    return (
      <div className="space-y-4">
        <Bilingual labelEs="Etiqueta" labelEn="Label" valueEs={data.label_es} valueEn={data.label_en} onEs={set('label_es')} onEn={set('label_en')} />
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={set('title_es')} onEn={set('title_en')} />
        <Bilingual labelEs="Subtítulo" labelEn="Subtitle" valueEs={data.subtitle_es} valueEn={data.subtitle_en} onEs={set('subtitle_es')} onEn={set('subtitle_en')} multiline />
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Imagen de fondo</label>
          <ImageUploader value={data.image_url || ''} onChange={set('image_url')} />
        </div>
        <Bilingual labelEs="Texto botón" labelEn="Button text" valueEs={data.cta_text_es} valueEn={data.cta_text_en} onEs={set('cta_text_es')} onEn={set('cta_text_en')} />
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">URL botón</label>
          <UrlField value={data.cta_url} onChange={set('cta_url')} placeholder="/es/directorio o un PDF de Archivos" />
        </div>
      </div>
    );
  }
  if (kind === 'text') {
    return (
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Contenido (ES)</label>
          <RichTextEditor value={data.html_es || ''} onChange={set('html_es')} />
        </div>
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Contenido (EN)</label>
          <RichTextEditor value={data.html_en || ''} onChange={set('html_en')} />
        </div>
      </div>
    );
  }
  if (kind === 'image') {
    return (
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Imagen</label>
          <ImageUploader value={data.url || ''} onChange={set('url')} />
        </div>
        <Bilingual labelEs="Alt" labelEn="Alt" valueEs={data.alt_es} valueEn={data.alt_en} onEs={set('alt_es')} onEn={set('alt_en')} />
        <Bilingual labelEs="Caption" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en} onEs={set('caption_es')} onEn={set('caption_en')} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!data.full_width} onChange={(e) => set('full_width')(e.target.checked)} className="rounded text-cmic-600" />
          Ancho completo
        </label>
      </div>
    );
  }
  if (kind === 'cta') {
    return (
      <div className="space-y-4">
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={set('title_es')} onEn={set('title_en')} />
        <Bilingual labelEs="Descripción" labelEn="Body" valueEs={data.body_es} valueEn={data.body_en} onEs={set('body_es')} onEn={set('body_en')} multiline />
        <Bilingual labelEs="Texto botón" labelEn="Button" valueEs={data.button_text_es} valueEn={data.button_text_en} onEs={set('button_text_es')} onEn={set('button_text_en')} />
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">URL botón</label>
          <UrlField value={data.button_url} onChange={set('button_url')} placeholder="/es/contacto o un PDF de Archivos" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Variante</label>
          <select value={data.variant || 'light'} onChange={(e) => set('variant')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm">
            <option value="light">Fondo claro</option>
            <option value="dark">Fondo oscuro</option>
          </select>
        </div>
      </div>
    );
  }
  if (kind === 'spacer') {
    return (
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Altura (px)</label>
        <input type="number" min={10} max={400} value={data.height || 60} onChange={(e) => set('height')(parseInt(e.target.value, 10) || 60)} className="mt-1 w-32 rounded-md border border-zinc-300 px-3 py-2 text-sm" />
      </div>
    );
  }
  if (kind === 'map') {
    return (
      <div className="space-y-4">
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={set('title_es')} onEn={set('title_en')} />
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">URL iframe Google Maps (opcional)</label>
          <input value={data.embed_url || ''} onChange={(e) => set('embed_url')(e.target.value)} placeholder="https://www.google.com/maps/embed?..." className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Lat</label>
            <input value={data.latitude || ''} onChange={(e) => set('latitude')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Lng</label>
            <input value={data.longitude || ''} onChange={(e) => set('longitude')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Zoom</label>
            <input type="number" value={data.zoom || 14} onChange={(e) => set('zoom')(parseInt(e.target.value, 10) || 14)} className="mt-1 w-full rounded-md border border-zinc-300 px-2 py-2 text-sm" />
          </div>
        </div>
        <Bilingual labelEs="Leyenda" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en} onEs={set('caption_es')} onEn={set('caption_en')} />
      </div>
    );
  }
  if (kind === 'contact_form') {
    return (
      <div className="space-y-4">
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={set('title_es')} onEn={set('title_en')} />
        <Bilingual labelEs="Subtítulo" labelEn="Subtitle" valueEs={data.subtitle_es} valueEn={data.subtitle_en} onEs={set('subtitle_es')} onEn={set('subtitle_en')} multiline />
      </div>
    );
  }
  if (kind === 'board_members') {
    return (
      <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1.5">
        <p className="font-medium text-zinc-800">Sección del Consejo Directivo (dinámica)</p>
        <p>Esta sección muestra automáticamente a los integrantes del consejo. Para editar los miembros, sus fotos y cargos, ve a <strong>Comunidad → Consejo Directivo</strong>. Aquí solo puedes reordenar o quitar el bloque.</p>
      </div>
    );
  }
  if (kind === 'past_presidents') {
    return (
      <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1.5">
        <p className="font-medium text-zinc-800">Sección de Expresidentes (dinámica)</p>
        <p>Muestra automáticamente el Premio Malintzin y la línea de presidentes. Para editarlos ve a <strong>Comunidad → Expresidentes</strong>. Aquí solo puedes reordenar o quitar el bloque.</p>
      </div>
    );
  }
  if (kind === 'periodicals_list') {
    return (
      <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1.5">
        <p className="font-medium text-zinc-800">Sección de Publicaciones (dinámica)</p>
        <p>Muestra el catálogo de publicaciones con buscador. Para editar las publicaciones ve a <strong>Comunidad → Publicaciones</strong>. Aquí solo puedes reordenar o quitar el bloque.</p>
      </div>
    );
  }
  if (kind === 'embedded_member_list') {
    return (
      <div className="space-y-4">
        <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en} onEs={set('title_es')} onEn={set('title_en')} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Límite</label>
            <input type="number" value={data.limit || 9} onChange={(e) => set('limit')(parseInt(e.target.value, 10) || 9)} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Layout</label>
            <select value={data.layout || 'grid'} onChange={(e) => set('layout')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm">
              <option value="grid">Grid</option>
              <option value="list">Lista</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Filtrar por idioma</label>
          <input value={data.filter_language || ''} onChange={(e) => set('filter_language')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Filtrar por ciudad</label>
          <input value={data.filter_city || ''} onChange={(e) => set('filter_city')(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!data.random_order} onChange={(e) => set('random_order')(e.target.checked)} data-testid="member-list-random-toggle" className="rounded text-cmic-600" />
          Mostrar intérpretes en orden aleatorio
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!data.show_view_all} onChange={(e) => set('show_view_all')(e.target.checked)} className="rounded text-cmic-600" />
          Mostrar enlace "Ver directorio completo"
        </label>
      </div>
    );
  }
  if (kind === 'html') {
    return (
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">HTML (ES)</label>
          <textarea value={data.html_es || ''} onChange={(e) => set('html_es')(e.target.value)} rows={8} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-mono" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">HTML (EN)</label>
          <textarea value={data.html_en || ''} onChange={(e) => set('html_en')(e.target.value)} rows={8} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-mono" />
        </div>
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">El HTML se sanitiza al renderizar.</p>
      </div>
    );
  }
  if (kind === 'stats') {
    return <StatsFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'cards') {
    return <CardsFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'accordion') {
    return <AccordionFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'testimonials') {
    return <TestimonialsFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'video') {
    return <VideoBlockFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'gallery') {
    return <GalleryBlockFields data={data} onUpdate={onUpdate} />;
  }
  if (kind === 'president_message') {
    return <PresidentMessageFields data={data} onUpdate={onUpdate} />;
  }
  // Generic fallback: dump JSON editor for arrays/complex kinds
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-2">Editor avanzado (JSON) para "{kind}":</p>
      <textarea
        value={JSON.stringify(data, null, 2)}
        onChange={(e) => {
          try { onUpdate(JSON.parse(e.target.value) || {}); } catch (_) { /* invalid JSON — ignore */ }
        }}
        rows={14}
        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-xs font-mono"
      />
      <p className="text-[10px] text-zinc-400 mt-1">Para edición más amigable de este tipo, usa el editor clásico (botón "Editor clásico" en la página principal).</p>
    </div>
  );
}

/* ============================================================
   ITEM-LIST FIELD EDITORS (stats, cards, accordion, testimonials, etc.)
   ============================================================ */

/**
 * Compact array-of-objects editor. Renders the list with add / remove /
 * reorder controls and delegates per-item rendering to `renderItem`.
 */
function ItemList({ items, onChange, emptyLabel, addLabel, renderItem, blankItem }) {
  function update(idx, patch) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function add() { onChange([...items, blankItem()]); }
  function remove(idx) { onChange(items.filter((_, i) => i !== idx)); }
  function move(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const out = items.slice();
    [out[idx], out[j]] = [out[j], out[idx]];
    onChange(out);
  }
  return (
    <div className="space-y-2.5">
      {items.length === 0 && (
        <p className="text-[11px] text-zinc-400 italic text-center py-3 border border-dashed border-zinc-200 rounded-md">
          {emptyLabel}
        </p>
      )}
      {items.map((it, idx) => (
        <div key={idx} className="border border-zinc-200 rounded-md p-2.5 bg-zinc-50/60 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">#{idx + 1}</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30" title="Subir">
                <ChevronUp size={12} />
              </button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30" title="Bajar">
                <ChevronDown size={12} />
              </button>
              <button type="button" onClick={() => remove(idx)} className="p-1 rounded text-red-500 hover:bg-red-50" title="Eliminar">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          {renderItem(it, (patch) => update(idx, patch))}
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-white border border-dashed border-zinc-300 text-cmic-700 rounded-md hover:bg-cmic-50 hover:border-cmic-300"
      >
        <Plus size={12} /> {addLabel}
      </button>
    </div>
  );
}

function MiniInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-xs focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none"
    />
  );
}

function MiniLabel({ children }) {
  return <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-0.5">{children}</span>;
}

function StatsFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-zinc-500 leading-relaxed bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2">
        Cada estadística tiene un número (valor) y una etiqueta bilingüe (ES/EN).
      </p>
      <ItemList
        items={items}
        onChange={(next) => onUpdate({ items: next })}
        emptyLabel="Sin estadísticas. Agrega la primera."
        addLabel="Agregar estadística"
        blankItem={() => ({ value: '', label_es: '', label_en: '' })}
        renderItem={(it, set) => (
          <div className="grid grid-cols-3 gap-2">
            <div>
              <MiniLabel>Valor</MiniLabel>
              <MiniInput value={it.value} onChange={(v) => set({ value: v })} placeholder="80+" />
            </div>
            <div>
              <MiniLabel>Etiqueta ES</MiniLabel>
              <MiniInput value={it.label_es} onChange={(v) => set({ label_es: v })} placeholder="Miembros" />
            </div>
            <div>
              <MiniLabel>Etiqueta EN</MiniLabel>
              <MiniInput value={it.label_en} onChange={(v) => set({ label_en: v })} placeholder="Members" />
            </div>
          </div>
        )}
      />
    </div>
  );
}

function CardsFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Título sección" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={(v) => onUpdate({ title_es: v })} onEn={(v) => onUpdate({ title_en: v })} />
      <div>
        <MiniLabel>Tarjetas</MiniLabel>
        <ItemList
          items={items}
          onChange={(next) => onUpdate({ items: next })}
          emptyLabel="Sin tarjetas. Agrega la primera."
          addLabel="Agregar tarjeta"
          blankItem={() => ({ title_es: '', title_en: '', body_es: '', body_en: '', link_url: '', file_url: '', file_name: '' })}
          renderItem={(it, set) => (
            <div className="space-y-2">
              <Bilingual labelEs="Título" labelEn="Title"
                valueEs={it.title_es ?? it.title ?? ''} valueEn={it.title_en ?? ''}
                onEs={(v) => set({ title_es: v })} onEn={(v) => set({ title_en: v })} />
              <Bilingual labelEs="Texto" labelEn="Body" multiline
                valueEs={it.body_es ?? it.body ?? ''} valueEn={it.body_en ?? ''}
                onEs={(v) => set({ body_es: v })} onEn={(v) => set({ body_en: v })} />
              <div>
                <MiniLabel>Enlace / URL (opcional)</MiniLabel>
                <MiniInput value={it.link_url} onChange={(v) => set({ link_url: v })} placeholder="/es/eventos o https://…" />
              </div>
              <div>
                <MiniLabel>Archivo descargable (opcional)</MiniLabel>
                <DocumentUploader
                  value={it.file_url}
                  fileName={it.file_name}
                  onChange={({ url, name }) => set({ file_url: url, file_name: name })}
                  testid="card-doc"
                />
                <p className="text-[10px] text-zinc-400 mt-1 leading-snug">
                  Si subes un archivo, al hacer clic en la tarjeta se descargará (tiene prioridad sobre el enlace). PDF, Word, Excel, PPT… máx. 20&nbsp;MB.
                </p>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function AccordionFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Título sección" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={(v) => onUpdate({ title_es: v })} onEn={(v) => onUpdate({ title_en: v })} />
      <div>
        <MiniLabel>Preguntas y respuestas</MiniLabel>
        <ItemList
          items={items}
          onChange={(next) => onUpdate({ items: next })}
          emptyLabel="Sin preguntas. Agrega la primera."
          addLabel="Agregar pregunta"
          blankItem={() => ({ q_es: '', q_en: '', a_es: '', a_en: '' })}
          renderItem={(it, set) => (
            <div className="space-y-2">
              <div>
                <MiniLabel>Pregunta ES</MiniLabel>
                <MiniInput value={it.q_es} onChange={(v) => set({ q_es: v })} />
              </div>
              <div>
                <MiniLabel>Pregunta EN</MiniLabel>
                <MiniInput value={it.q_en} onChange={(v) => set({ q_en: v })} />
              </div>
              <div>
                <MiniLabel>Respuesta ES</MiniLabel>
                <textarea value={it.a_es || ''} onChange={(e) => set({ a_es: e.target.value })} rows={3}
                  className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <MiniLabel>Respuesta EN</MiniLabel>
                <textarea value={it.a_en || ''} onChange={(e) => set({ a_en: e.target.value })} rows={3}
                  className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-xs" />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function TestimonialsFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Título sección" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={(v) => onUpdate({ title_es: v })} onEn={(v) => onUpdate({ title_en: v })} />
      <div>
        <MiniLabel>Testimonios</MiniLabel>
        <ItemList
          items={items}
          onChange={(next) => onUpdate({ items: next })}
          emptyLabel="Sin testimonios."
          addLabel="Agregar testimonio"
          blankItem={() => ({ quote: '', author: '', photo_url: '' })}
          renderItem={(it, set) => (
            <div className="space-y-2">
              <div>
                <MiniLabel>Cita</MiniLabel>
                <textarea value={it.quote || ''} onChange={(e) => set({ quote: e.target.value })} rows={3}
                  className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <MiniLabel>Autor</MiniLabel>
                <MiniInput value={it.author} onChange={(v) => set({ author: v })} placeholder="Nombre del autor" />
              </div>
              <div>
                <MiniLabel>Foto del autor (opcional)</MiniLabel>
                <ImageUploader value={it.photo_url || ''} onChange={(url) => set({ photo_url: url })} shape="circle" />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function VideoBlockFields({ data, onUpdate }) {
  const set = (k) => (v) => onUpdate({ [k]: v });
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Título" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={set('title_es')} onEn={set('title_en')} />
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">URL del video (YouTube o Vimeo)</label>
        <input value={data.url || ''} onChange={(e) => set('url')(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
      </div>
      <Bilingual labelEs="Leyenda" labelEn="Caption" valueEs={data.caption_es} valueEn={data.caption_en}
        onEs={set('caption_es')} onEn={set('caption_en')} />
    </div>
  );
}

function GalleryBlockFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Título sección" labelEn="Section title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={(v) => onUpdate({ title_es: v })} onEn={(v) => onUpdate({ title_en: v })} />
      <div>
        <MiniLabel>Imágenes</MiniLabel>
        <ItemList
          items={items}
          onChange={(next) => onUpdate({ items: next })}
          emptyLabel="Sin imágenes. Agrega la primera."
          addLabel="Agregar imagen"
          blankItem={() => ({ url: '', caption: '' })}
          renderItem={(it, set) => (
            <div className="space-y-2">
              <ImageUploader value={it.url || ''} onChange={(url) => set({ url })} />
              <div>
                <MiniLabel>Caption (opcional)</MiniLabel>
                <MiniInput value={it.caption} onChange={(v) => set({ caption: v })} />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function PresidentMessageFields({ data, onUpdate }) {
  const set = (k) => (v) => onUpdate({ [k]: v });
  return (
    <div className="space-y-4">
      <Bilingual labelEs="Rol (encabezado)" labelEn="Role (header)" valueEs={data.role_es} valueEn={data.role_en}
        onEs={set('role_es')} onEn={set('role_en')} />
      <Bilingual labelEs="Mensaje" labelEn="Message" valueEs={data.body_es} valueEn={data.body_en}
        onEs={set('body_es')} onEn={set('body_en')} multiline />
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Nombre completo</label>
        <input value={data.name || ''} onChange={(e) => set('name')(e.target.value)}
          className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
      </div>
      <Bilingual labelEs="Cargo" labelEn="Title" valueEs={data.title_es} valueEn={data.title_en}
        onEs={set('title_es')} onEn={set('title_en')} />
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Periodo</label>
        <input value={data.period || ''} onChange={(e) => set('period')(e.target.value)}
          placeholder="Bienio 2026-2028"
          className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Foto</label>
        <ImageUploader value={data.photo_url || ''} onChange={set('photo_url')} shape="circle" />
      </div>
    </div>
  );
}

